import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { MessageCircle, Phone, MapPin, Sparkles } from 'lucide-react';
import { aboutInfo } from '../mock';
import { Fireflies, Moth } from '../components/FairyElements';

const About = () => {
  const whatsappLink = `https://wa.me/91${aboutInfo.phone}`;

  return (
    <div className="min-h-screen bg-[#0a1612]">
      <section className="forest-bg min-h-[600px] px-4 md:px-8 lg:px-16 py-20 flex items-center" style={{ '--forest-image': "url(/mystical-garden/hero-forest.png)", '--bg-opacity': 0.55}}>
        <Fireflies count={50} />
        <Moth delay={0} top="20%" />
        <Moth delay={6} top="55%" />

        <div className="max-w-7xl mx-auto relative z-10 w-full">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="animate-fadeIn">
              <Badge className="mb-4 bg-emerald-900/60 border border-amber-500/40 text-amber-300 backdrop-blur-md">
                <Sparkles className="w-3 h-3 mr-1.5" />
                <span className="tracking-widest text-xs uppercase">{aboutInfo.title}</span>
              </Badge>
              <h1 className="text-6xl md:text-7xl font-bold mb-4 gradient-text text-glow">{aboutInfo.name}</h1>
              <p className="text-2xl text-amber-300 font-semibold mb-6 font-serif-mystical italic">{aboutInfo.aka}</p>
              <div className="space-y-3 text-amber-100/80 mb-8">
                <div className="flex items-center">
                  <MapPin className="w-5 h-5 mr-3 text-amber-500" />
                  <span>{aboutInfo.location}</span>
                </div>
                <div className="flex items-center">
                  <Phone className="w-5 h-5 mr-3 text-amber-500" />
                  <span>{aboutInfo.phone}</span>
                </div>
              </div>
              <Button size="lg" className="btn-gold px-10 py-7" onClick={() => window.open(whatsappLink, '_blank')}>
                <MessageCircle className="mr-2 h-5 w-5" />
                Connect on WhatsApp
              </Button>
            </div>

            <div className="relative">
              <div className="aspect-square rounded-2xl overflow-hidden shadow-2xl border-4 border-amber-500/40 mystical-glow-gold">
                <img
                  src="/mystical-garden/about-portrait.png"
                  alt="Meeta Jain - The Divine7777"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 px-5 py-2 bg-amber-500/95 text-emerald-950 rounded-full font-semibold text-sm backdrop-blur-md border border-amber-300 shadow-xl" style={{ fontFamily: 'Cinzel, serif' }}>
                Mystic Guide
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-deep py-24 px-4 md:px-8 lg:px-16 relative">
        <Fireflies count={20} />
        <div className="max-w-4xl mx-auto relative z-10">
          <h2 className="text-5xl font-bold mb-12 text-center gradient-text">About Me</h2>
          <div className="space-y-6 text-amber-100/85 leading-relaxed text-lg font-serif-mystical">
            {aboutInfo.bio.split('\n\n').map((paragraph, idx) => (
              <p key={idx}>{paragraph}</p>
            ))}
          </div>
        </div>
      </section>

      <section className="forest-bg py-24 px-4 md:px-8 lg:px-16 relative" style={{ '--forest-image': "url(/mystical-garden/services-grove.png)", '--bg-opacity': 0.3}}>
        <Fireflies count={25} />
        <div className="max-w-6xl mx-auto relative z-10">
          <h2 className="text-5xl font-bold mb-16 text-center gradient-text">My Specializations</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {aboutInfo.specializations.map((specialization, idx) => (
              <Card key={idx} className="forest-card border-0 text-center">
                <CardHeader>
                  <div className="mx-auto mb-3 w-14 h-14 bg-gradient-to-br from-emerald-700 to-amber-600 rounded-full flex items-center justify-center mystical-glow-gold border border-amber-500/40">
                    <Sparkles className="w-7 h-7 text-amber-200" />
                  </div>
                  <CardTitle className="text-lg text-amber-200" style={{ fontFamily: 'Cinzel, serif' }}>{specialization}</CardTitle>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="section-deep py-24 px-4 md:px-8 lg:px-16 relative">
        <Fireflies count={20} />
        <div className="max-w-5xl mx-auto relative z-10">
          <h2 className="text-5xl font-bold mb-12 text-center gradient-text">My Approach</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { title: 'Intuitive', desc: 'Combining ancient wisdom with deep intuitive insights for accurate and meaningful readings.' },
              { title: 'Compassionate', desc: 'Providing guidance with empathy, understanding, and non-judgmental support for your journey.' },
              { title: 'Empowering', desc: 'Helping you make informed decisions and navigate challenges with clarity and confidence.' }
            ].map((item, idx) => (
              <Card key={idx} className="forest-card border-0 text-center">
                <CardHeader>
                  <CardTitle className="text-2xl text-amber-200" style={{ fontFamily: 'Cinzel, serif' }}>{item.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-amber-100/70 text-center">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="forest-bg py-24 px-4 md:px-8 lg:px-16 relative" style={{ '--forest-image': "url(/mystical-garden/hero-forest.png)", '--bg-opacity': 0.5}}>
        <Fireflies count={50} />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-4xl md:text-6xl font-bold mb-6 gradient-text text-glow">Ready to Start Your Journey?</h2>
          <p className="text-lg mb-10 text-amber-100/80 font-serif-mystical italic">
            Let's connect and discover the guidance you're seeking in the mystical garden.
          </p>
          <Button size="lg" className="btn-gold px-10 py-7" onClick={() => window.open(whatsappLink, '_blank')}>
            <MessageCircle className="mr-2 h-5 w-5" />
            Book Your Reading
          </Button>
        </div>
      </section>
    </div>
  );
};

export default About;
